import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ModalComponent } from '@connect-services/client/components/modal/modal.component';
import { ProfileModalComponent } from '@connect-services/client/components/profile-modal/profile-modal.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { PermissionAliasEnum } from '@connect-services/client/sdk/api';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-profile-icon',
    templateUrl: './profile-icon.component.html',
})
export class ProfileIconComponent {
    public readonly iconClasses = computed(() => ({
        'text-custom-blue': this.isActive(),
        'text-custom-light-blue': !this.isActive(),
    }));
    public readonly imageClasses = computed(() => ({
        'bg-custom-blue': this.isActive(),
        'bg-custom-light-blue': !this.isActive(),
    }));
    public readonly isMenuOpen = signal<boolean>(false);
    public readonly isProfileActive = signal<boolean>(false);
    public readonly panelService = inject(PanelService);

    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly securityService = inject(SecurityService);

    private readonly editModeStateService = inject(EditModeStateService);
    private readonly isActive = computed(() => this.isMenuOpen() || this.isProfileActive());
    private readonly matDialog = inject(MatDialog);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly translocoService = inject(TranslocoService);

    public onMenuClosed(): void {
        this.isMenuOpen.set(false);
    }

    public onMenuOpened(): void {
        this.isMenuOpen.set(true);
    }

    public openLogoutDialog(): void {
        this.isMenuOpen.set(false);
        this.isProfileActive.set(false);

        const dialogRef = this.matDialog.open(ModalComponent, {
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('logout'),
                description: this.buildLogoutDescription(),
                title: this.translocoService.translate('warning'),
                type: ModalType.DANGER,
            },
        });

        dialogRef.afterClosed().subscribe(async (result: ModalResultEnum) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }

            await this.securityService.logout();
        });
    }

    public openProfileModal(): void {
        this.isMenuOpen.set(false);
        this.isProfileActive.set(true);

        const dialogRef = this.matDialog.open(ProfileModalComponent, {
            autoFocus: false,
            height: '28rem',
            width: '50rem',
        });

        dialogRef.afterClosed().subscribe(() => {
            this.isProfileActive.set(false);
        });
    }
    private buildLogoutDescription(): string {
        const baseMessage = this.translocoService.translate('doYouWantToLogout');
        const warning = this.subWarning();

        if (!warning) {
            return baseMessage;
        }

        return `${baseMessage}\n${warning}`;
    }

    private subWarning(): string | undefined {
        const projectId = this.projectBloc.value()?.id;
        if (!projectId) {
            return;
        }

        const pendingCount = this.editModeStateService.getPendingEditCount(projectId);
        if (!pendingCount) {
            return;
        }
        const description = this.translocoService.translate('unsavedEditsWillBeLost');

        return `${description} (${pendingCount})`;
    }
}
